import dayjs from 'dayjs';
import humanizeDuration from 'humanize-duration';

export const API_DATE_FORMAT = 'YYYY-MM-DD';
export const SHORT_DATE_FORMAT = 'DD.MM.YYYY';
export const API_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
export const SHORT_DATETIME_FORMAT = 'DD.MM.YYYY HH:mm';

export const humanizedHours = (hours: number) => {
  const milliseconds = hours * 60 * 60 * 1000;
  return humanizeDuration(milliseconds, { language: 'de', units: ['d', 'h'], round: true });
};

// Short German variant, e.g. "3 Tage, 24 Std" instead of "3 Tage, 24 Stunden".
const shortHumanizer = humanizeDuration.humanizer({
  language: 'deShort',
  languages: {
    deShort: {
      d: (count?: number) => (count === 1 ? 'Tag' : 'Tage'),
      h: () => 'Std',
    },
  },
  units: ['d', 'h'],
  round: true,
});

export const humanizedHoursShort = (hours: number) => shortHumanizer(hours * 60 * 60 * 1000);

export const personAge = (birthday: string): number => Math.round(dayjs().diff(birthday, 'years', true) * 10) / 10;

const FILE_DATE_FORMAT = 'YYYY-MM-DD';
const FILE_DATETIME_FORMAT = 'YYYY-MM-DD_HHmm';
const SHORT_TIME_FORMAT = 'HH:mm';
const COMPACT_DATE_FORMAT = 'DD.MM.YY';
const COMPACT_DATETIME_FORMAT = 'DD.MM.YY HH:mm';

// Ranges covering whole days are shown without time, so reports that are not restricted to a time
// of day keep looking as they always did. Compared with second precision, because that is what the
// API receives — anything finer would make the label disagree with the one the PDF carries.
const coversWholeDays = (from: dayjs.Dayjs, to: dayjs.Dayjs) =>
  from.isSame(from.startOf('day'), 'second') && !to.isBefore(to.hour(23).minute(59).second(0), 'second');

const rangeLabel = (from: dayjs.Dayjs, to: dayjs.Dayjs, dateFormat: string, dateTimeFormat: string): string => {
  if (!coversWholeDays(from, to)) {
    // the date is only repeated when the range actually spans multiple days
    return from.isSame(to, 'day')
      ? `${from.format(dateTimeFormat)} - ${to.format(SHORT_TIME_FORMAT)}`
      : `${from.format(dateTimeFormat)} - ${to.format(dateTimeFormat)}`;
  }
  if (from.isSame(to, 'day')) {
    return from.format(dateFormat);
  }
  return `${from.format(dateFormat)} - ${to.format(dateFormat)}`;
};

export const orderReportRangeLabel = (from: dayjs.Dayjs, to: dayjs.Dayjs): string =>
  rangeLabel(from, to, SHORT_DATE_FORMAT, SHORT_DATETIME_FORMAT);

// Two-digit year variant for tight spots — the report tile is only about a third of the
// dashboard wide, where the full year pushes a day-spanning range past the box.
export const orderReportRangeLabelCompact = (from: dayjs.Dayjs, to: dayjs.Dayjs): string =>
  rangeLabel(from, to, COMPACT_DATE_FORMAT, COMPACT_DATETIME_FORMAT);

// Open orders are not filtered by the selected range, so their report is labelled with the moment
// it was generated instead.
export const orderReportSnapshotLabel = (now: dayjs.Dayjs): string => `Stand: ${now.format(SHORT_DATETIME_FORMAT)}`;

export const orderReportSnapshotFileNamePart = (now: dayjs.Dayjs): string => now.format(FILE_DATE_FORMAT);

export const orderReportRangeFileNamePart = (from: dayjs.Dayjs, to: dayjs.Dayjs): string => {
  if (!coversWholeDays(from, to)) {
    return `${from.format(FILE_DATETIME_FORMAT)}-${to.format(FILE_DATETIME_FORMAT)}`;
  }
  if (from.isSame(to, 'day')) {
    return from.format(FILE_DATE_FORMAT);
  }
  return `${from.format(FILE_DATE_FORMAT)}-${to.format(FILE_DATE_FORMAT)}`;
};
